import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import SEO from "@/components/SEO";

export default function Cart() {
    const { items, remove, update, subtotal } = useCart();
    const { t, lang } = useI18n();
    const nav = useNavigate();

    if (items.length === 0) {
        return (
            <>
                <SEO title={t("cart")} />
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
                    <ShoppingBag className="w-14 h-14 mx-auto text-brand-royal/40" />
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mt-4 text-brand-navy">{t("empty_cart")}</h2>
                    <Link
                        to="/books/written"
                        className="btn-primary mt-6 inline-block px-6 py-3 rounded-xl font-semibold"
                    >
                        {t("explore_books")}
                    </Link>
                </div>
            </>
        );
    }

    return (
        <>
            <SEO title={t("cart")} />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-8 overflow-x-hidden">
            <div className="lg:col-span-2 space-y-3">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-brand-navy">{t("cart")}</h1>

                {items.map((it) => (
                    <div key={it.book_id} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-4 bg-white border border-brand-light rounded-2xl">
                        <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
                            <img src={it.cover_image} alt={it.title} className="w-16 sm:w-20 h-20 sm:h-24 object-cover rounded-lg shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-brand-navy line-clamp-2 text-sm sm:text-base">{it.title}</div>
                                <div className="text-xs sm:text-sm text-brand-royal mt-1">৳{it.price}</div>
                            </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3 w-full sm:w-auto sm:ml-auto">
                            <div className="flex items-center border border-brand-light rounded-xl">
                                <button onClick={() => update(it.book_id, it.quantity - 1)} className="px-3 py-1.5 hover:bg-brand-light/40"><Minus className="w-3 h-3 sm:w-3.5 sm:h-3.5" /></button>
                                <span className="px-3 font-semibold text-sm sm:text-base">{it.quantity}</span>
                                <button onClick={() => update(it.book_id, it.quantity + 1)} className="px-3 py-1.5 hover:bg-brand-light/40"><Plus className="w-3 h-3 sm:w-3.5 sm:h-3.5" /></button>
                            </div>
                            <div className="font-bold text-brand-navy text-right whitespace-nowrap text-sm sm:text-base">৳{it.price * it.quantity}</div>
                            <button onClick={() => remove(it.book_id)} className="p-2 text-brand-red hover:bg-brand-red/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                    </div>
                ))}

            </div>

            <aside className="lg:col-span-1">
                <div className="glass rounded-2xl p-6 sticky top-32">
                    <h3 className="font-bold text-brand-navy text-lg">
                        {t("order_summary")}
                    </h3>

                    <div className="mt-4 space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span>{t("subtotal")}</span>
                            <span>৳{subtotal}</span>
                        </div>

                        <div className="flex justify-between text-brand-navy/60">
                            <span>{t("delivery_fee")}</span>
                            <span>
                                {lang === "bn" ? "চেকআউটে দেখুন" : "At checkout"}
                            </span>
                        </div>

                        <div className="border-t border-brand-light mt-3 pt-3 flex justify-between font-bold text-base text-brand-royal">
                            <span>{t("total")}</span>
                            <span>৳{subtotal}</span>
                        </div>
                    </div>

                    <button
                        onClick={() => nav("/checkout")}
                        className="btn-primary w-full mt-6 py-3 rounded-xl font-semibold"
                        data-testid="cart-checkout-btn"
                    >
                        {t("checkout")} →
                    </button>
                </div>
            </aside>
        </div>
        </>
    );
}
